import { Suspense } from 'react';
import PropTypes from 'prop-types';
import { useAuth } from '../components/AuthContext';  
import { Typography } from "@material-tailwind/react";
import { Link } from 'react-router-dom'; 
import { reverse } from '../pages/urls'; // Import the reverse function


// Define the SOCIAL_ICONS array with icon names as strings
// Importing icons from Material-UI
import XIcon from '@mui/icons-material/X';
import FacebookIcon from '@mui/icons-material/Facebook';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import InstagramIcon from '@mui/icons-material/Instagram';
import YouTubeIcon from '@mui/icons-material/YouTube';
import FavoriteIcon from '@mui/icons-material/Favorite';

// Static mapping of icon names to their respective components
const Icons = {
  Facebook: FacebookIcon,
  Instagram: InstagramIcon,
  LinkedIn: LinkedInIcon,
  X: XIcon,
  YouTube: YouTubeIcon,
};

const SOCIAL_ICONS = [
  { url: "https://www.facebook.com/RelightSA/", icon: 'Facebook' },
  // { url: "https://www.instagram.com/relightsa/", icon: 'Instagram' },
  // { url: "https://youtube.com/channel/UCgL1iZSOc4WWNDad_xtalqA", icon: 'YouTube' },
  // { url: "https://za.linkedin.com/in/madelinepate", icon: 'LinkedIn' },
  // { url: "https://x.com/RelightSA/", icon: 'X' },
];


const HTMLRenderer = ({ htmlContent }) => {
  return (
    <span dangerouslySetInnerHTML={{
      __html: htmlContent.replace(/<0>/g, '<b>').replace(/<\/0>/g, '</b>')
    }} />
  );
};

HTMLRenderer.propTypes = {
  htmlContent: PropTypes.string.isRequired,
};

const Footer = () => {
  const { isAuthorized } = useAuth();
  const currentYear = new Date().getFullYear();

  const markFooterNavigation = (linkUrl, target) => {
    if (
      target
      || !linkUrl
      || linkUrl.startsWith('http')
      || linkUrl.startsWith('mailto:')
      || linkUrl.startsWith('tel:')
    ) {
      return;
    }

    sessionStorage.setItem('pendingFooterScrollToTop', 'true');
  };

  const SITEMAP = [
    {
      title: "Order Information",
      links: [
        { name: "How to Order", url: "/how-to-order" },
        { name: "Delivery Information", url: "/delivery-info" },
        { name: "Return Policy", url: "/return-policy" },
        { name: "Shipping Policy", url: "/shipping-policy" },
        { name: "Terms and Conditions", url: "/terms-and-conditions" },
        { name: "Privacy Policy", url: "/privacy-policy" },
        { name: "Disclaimer", url: "/disclaimer" }
      ],
    },
    {
      title: "Trading Hours",
      content: (
        <div className="space-y-2">
          <Typography
            variant="small"
            color="blue-gray"
            className="font-normal text-white/90 text-sm"
          >
            <b>Monday to Friday:</b>
            <br /> 8am to 5:00pm
            <br />
            <br />
            <b>Saturday:</b>
            <br /> 8am to 1:00pm
            <br />
            <br />
            <b>Public Holidays:</b>
            <br /> 9am to 1pm
          </Typography>
        </div>
      ),
    },
    {
      title: "Contact Us",
      links: [
        { name: "Contact Us", reverse: "contact" },
        { name: "About Us", url: "/about-us" },
        { name: "\u00A0", disabled: true }, // Non-breaking space for visible empty row
        { name: "+27 (0) 82 562 6644", url: "tel:+27825626644" },
        { name: "john@relightsa.co.za", url: "mailto:john@relightsa.co.za" },
        { name: "\u00A0", disabled: true }, // Non-breaking space for visible empty row
        { name: "40 St Andrews Road", url: "https://maps.google.com/?q=40+St+Andrews+Road,+Claremont,+Cape+Town,+South+Africa" },
        { name: "Claremont, Cape Town", url: "https://maps.google.com/?q=40+St+Andrews+Road,+Claremont,+Cape+Town,+South+Africa" }
      ],
    },
    {
      title: "My Account",
      links: [
        { name: "Login", reverse: "login", auth: false },
        { name: "Update Profile", reverse: "profile", auth: true },
        { name: "My Orders", reverse: "orders", auth: true },
        { name: "Forgot Password", reverse: "forgot_password", auth: false },
        { name: "Register", reverse: "register", auth: false },
        { name: "My Returns", reverse: "returns", auth: true },
        { name: "Change Email", reverse: "change_email", auth: true },
        { name: "Change Password", reverse: "change_password", auth: true },
        { name: "Logout", reverse: "logout", auth: true },
        { name: "My Basket", reverse: "basket", auth: true }
      ],
    },
    {
      title: "Help / Support",
      links: [
        { name: "Frequently Asked Questions", url: "/support/faq" },
        { name: "Make a Return", url: "/support/make-a-return" },
      ],
    },
    {
      title: "", // Empty block for spacing
      content: <div></div>,
    },
    {
      title: "", // Empty block for spacing
      content: <div></div>,
    },
    {
      title: "Follow Us",
      content: (
        <div className="flex gap-4 text-white justify-center md:justify-start">
          {SOCIAL_ICONS.map(({ url, icon }, key) => {
            const IconComponent = Icons[icon];
            return (
              <Suspense fallback={<div>Loading...</div>} key={key}>
                <Typography 
                  as="a" 
                  href={url} 
                  target="_blank" 
                  className="opacity-80 transition-opacity hover:opacity-100"
                >
                  <IconComponent className="text-white text-xl" />
                </Typography>
              </Suspense>
            );
          })}
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="w-full bg-white py-8">
        <div className="mx-auto max-w-7xl px-8">
          <div className="flex justify-center">
            <img 
              src="/img/payment-gateways.png" 
              alt="Payment Gateways" 
              className="max-w-full h-auto"
              style={{ maxHeight: '100px' }}
            />
          </div>
        </div>
      </div>
      <footer className="relative w-full bg-[#1e73be] dark:bg-[#1e73be] border-t border-blue-700/30">
        <div className="mx-auto w-full max-w-7xl px-8">
          <div className="mx-auto grid w-full grid-cols-1 gap-8 py-12 md:grid-cols-2 lg:grid-cols-4">
            {SITEMAP.map(({ title, links, content }, key) => (
              <div key={key} className="w-full text-center md:text-left">
                <Typography
                  variant="small"
                  className="mb-4 font-bold uppercase text-white/80"
                >
                  {title}
                </Typography>
                {content ? (
                  <div className="text-sm text-white">
                    {content}
                  </div>
                ) : (
                  <ul className="space-y-1">
                    {links?.map(({ name, target, reverse: reverseName, url, auth }, linkKey) => {
                      if (auth !== undefined && auth !== isAuthorized) return null;
                      const linkUrl = reverseName ? reverse(reverseName) : url;
                      return (
                        <Typography key={linkKey} as="li" className="font-normal">
                          <Link 
                            to={linkUrl} 
                            target={target} 
                            className="text-sm text-white/90 hover:text-white transition-colors duration-200"
                            onClick={() => markFooterNavigation(linkUrl, target)}
                          >
                            {name}
                          </Link>
                        </Typography>
                      );
                    })}
                  </ul>
                )}
              </div>
            ))}
          </div>
          <div className="flex w-full flex-col items-center justify-center border-t border-blue-700/30 py-4">
            <Typography
              variant="small"
              className="text-center font-normal text-white/90"
            >
              <HTMLRenderer htmlContent={`© ${currentYear} <0>RelightSA</0>. All Rights Reserved.`} /><br/>
              Built with <FavoriteIcon sx={{ fontSize: 14, verticalAlign: '-2px', mx: 0.25 }} /> by{' '}
              <a href="https://tensology.com" target="_blank" rel="noopener noreferrer">tensology.com</a>
            </Typography>
          </div>
        </div>
      </footer>
    </>
  );
};

export default Footer;
